import React, { useState } from 'react';
import { useStyletron } from 'baseui';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { REACT_APP_API_BASE_URL } from 'shared/config/config';
import { IEmailAttachmentsData, IObjProps } from 'shared/consts/types';
import { getSizeInMbOrKb, labelWithCharacterLimit } from 'shared/helpers';
import DownloadIcon from 'shared/icons/DownloadIcon';

import { RootState } from 'store/rootReducer';

const ATTACHMENT_NAME_LIMIT = 50;

const Attachments = ({
	attachments,
	attachmentFocusColor,
	colors,
	processName = false,
}: {
	attachments: Array<IEmailAttachmentsData>;
	attachmentFocusColor: string;
	colors: IObjProps;
	processName?: boolean;
}) => {
	const [css, theme]: [any, any] = useStyletron();
	const { t } = useTranslation(['components']);

	const cid = useSelector((state: RootState) => state.User.botAdmin.data.cid);

	const [hover, setHover] = useState<number>(-1);

	return (
		<>
			{attachments.length > 0 && (
				<>
					<div
						className={css({
							paddingTop: '0.75rem',
							fontWeight: 700,
							fontSize: '0.75rem',
							lineHeight: '1.25rem',
						})}
					>
						<span
							className={css({
								paddingRight: '0.25rem',
							})}
						>
							{attachments.length}
						</span>
						<span>
							{attachments.length > 1
								? t('components:emailMessagesPreview.attachments')
								: t('components:emailMessagesPreview.attachment')}
						</span>
					</div>
					<div>
						{attachments.map((attachment: IEmailAttachmentsData, index: number) => {
							const { isConversation, contentToken, attachmentName, attachmentSize } = attachment;
							const downloadUrl = isConversation
								? ''
								: `${REACT_APP_API_BASE_URL}/v2/${cid}/attachment/?token=${contentToken}&response_type=download_attachment`;
							const name =
								processName && attachmentName.includes(':')
									? attachmentName.split(':')[1]
									: attachmentName;

							return (
								<div
									key={contentToken}
									className={css({
										display: 'flex',
										height: '1.75rem',
										alignItems: 'center',
										borderTopRightRadius: '0.25rem',
										borderTopLeftRadius: '0.25rem',
										borderBottomLeftRadius: '0.25rem',
										borderBottomRightRadius: '0.25rem',
										marginTop: '0.25rem',
										':hover': {
											backgroundColor: attachmentFocusColor,
											paddingLeft: '0.5rem',
											paddingRight: '0.5rem',
											width: 'calc(100% + 1rem)',
											marginLeft: '-0.5rem',
											marginRight: '-0.5rem',
											boxSizing: 'border-box',
										},
									})}
									onMouseEnter={() => setHover(index)}
									onMouseLeave={() => setHover(-1)}
								>
									<div
										className={css({
											flexGrow: 1,
										})}
									>
										<span
											className={css({
												color: colors.text,
												paddingRight: '0.25rem',
												textDecoration: 'underline',
												cursor: 'pointer',
											})}
											onClick={() => {
												window.open(attachment.attachmentURL, '_blank');
											}}
										>
											{name.length > ATTACHMENT_NAME_LIMIT
												? labelWithCharacterLimit(ATTACHMENT_NAME_LIMIT, name)
												: name}
										</span>
										<span>{getSizeInMbOrKb(attachmentSize)}</span>
									</div>
									{hover === index && downloadUrl ? (
										<div
											className={css({
												width: '0.75rem',
												cursor: 'pointer',
											})}
										>
											<a
												target='self'
												download
												href={downloadUrl}
												className={css({
													display: 'flex',
													alignItems: 'center',
												})}
											>
												<DownloadIcon fillColor={colors.icon} size={12} />
											</a>
										</div>
									) : null}
								</div>
							);
						})}
					</div>
				</>
			)}
		</>
	);
};

export default React.memo(Attachments);
